// ============================================================
// TMR Portal Admin — a clinic application as the Nova empresa wizard opens it (28 §9)
// ============================================================
import { maskCEP, maskCNPJ, maskCPF } from '../../lib/format';
import { displayPhone } from '../../lib/phone';
import { INITIAL_COMPANY_FORM } from '../companies/form';
import { segmentFromSpecialty } from '../companies/segment';
import type { CompanyForm } from '../companies/form';
import type { CompanyApplicationDetail } from './contract';
import { revenueBandLabel, specialtyLabel } from './status';

/** The commercial origin an approved application gets unless the team says otherwise — the backend's default too. */
export const SITE_ACQUISITION_ORIGIN = 'Inbound (site)';

/**
 * Fills the wizard with what the clinic typed, so the team only completes what the site never asks —
 * subdomain, permissions, managers, classification — and corrects what it must. The segment opens chosen
 * when the clinic's specialty names one exactly (32 §9), and empty otherwise.
 *
 * Everything lands in the form's own masked shape (the wizard validates and strips masks the same way it does
 * for a typed registration). The contact becomes the focal contact: it is who the team has been talking to.
 * The bank block is pre-filled as far as it maps — the site does not ask for the bank's name, and keeps the
 * check digit inside the account number — so the team confirms it on its step before it is sent.
 *
 * @param application The application as `GET /company-applications/{id}` returned it.
 */
export function companyFormFromApplication(application: CompanyApplicationDetail): CompanyForm {
  const { clinic, contact, address, representative, bankAccount } = application;
  const account = bankAccount ? splitAccountNumber(bankAccount.accountNumber) : null;

  return {
    ...INITIAL_COMPANY_FORM,
    name: clinic.tradeName,
    legalName: clinic.legalName ?? '',
    cnpj: maskCNPJ(clinic.cnpj),
    acquisitionOrigin: SITE_ACQUISITION_ORIGIN,
    segment: segmentFromSpecialty(clinic.specialty) ?? '',
    description: applicationNotes(application),
    zipCode: address ? maskCEP(address.zipCode) : '',
    state: address?.state ?? '',
    city: address?.city ?? '',
    district: address?.district ?? '',
    street: address?.street ?? '',
    number: address?.number ?? '',
    complement: address?.complement ?? '',
    representativeName: representative?.name ?? '',
    representativeCpf: representative ? maskCPF(representative.cpf) : '',
    representativeEmail: representative?.email ?? '',
    representativePhone: displayPhone(representative?.phone),
    focalContactName: contact.name,
    focalContactEmail: contact.email,
    focalContactPhone: displayPhone(contact.phone),
    bankHolderLegalName: bankAccount ? clinic.legalName ?? '' : '',
    bankCode: bankAccount?.bankCode ?? '',
    bankAccountType: bankAccount?.accountType ?? '',
    bankBranch: bankAccount?.branch ?? '',
    bankAccountNumber: account?.number ?? '',
    bankAccountDigit: account?.digit ?? '',
  };
}

/**
 * The internal note the Empresa starts with: where it came from and what the clinic said about itself that
 * has no field of its own in the registration.
 */
function applicationNotes(application: CompanyApplicationDetail): string {
  const { clinic, bankAccount } = application;
  const lines = [
    `Solicitação ${application.protocol} pelo site.`,
    `Especialidade: ${specialtyLabel(clinic.specialty)}.`,
    `Faturamento declarado: ${revenueBandLabel(clinic.monthlyRevenueBand)}.`,
  ];
  if (clinic.instagram) lines.push(`Instagram: ${clinic.instagram}.`);
  if (clinic.website) lines.push(`Site: ${clinic.website}.`);
  if (bankAccount?.pixKey) lines.push(`Chave PIX informada: ${bankAccount.pixKey}.`);
  return lines.join('\n');
}

/**
 * The account number the site took in one field, split into number and check digit the way the wizard
 * holds them — the digit is what follows the last separator. Without one, everything stays in the number
 * and the team types the digit.
 *
 * @param raw The account number as typed on the site, e.g. `12345-6`.
 */
export function splitAccountNumber(raw: string): { number: string; digit: string } {
  const trimmed = raw.trim();
  const match = /^(.*\d)[\s.-]+([0-9xX]{1,2})$/.exec(trimmed);
  if (!match) return { number: trimmed, digit: '' };
  return { number: match[1], digit: match[2].toUpperCase() };
}
